import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Layers, Leaf, Plus, Search, Settings, X } from 'lucide-react';
import type { Bed, Plant, Zone } from '../types';
import { STATUS_BG, STATUS_LABELS, TYPE_EMOJI, cn } from '../lib/utils';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';

interface SidebarProps {
  zones: Zone[];
  beds: Bed[];
  plants: Plant[];
  selectedBedId: string | null;
  selectedPlantId: string | null;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onBedClick: (bed: Bed) => void;
  onPlantClick: (plant: Plant) => void;
  onAddBed: () => void;
  onAddPlant: () => void;
  onAddZone: () => void;
  onSettingsOpen: () => void;
}

export function Sidebar({
  zones,
  beds,
  plants,
  selectedBedId,
  selectedPlantId,
  searchQuery,
  onSearchChange,
  onBedClick,
  onPlantClick,
  onAddBed,
  onAddPlant,
  onAddZone,
  onSettingsOpen,
}: SidebarProps) {
  const [expandedZones, setExpandedZones] = useState<Set<string>>(new Set(zones.map((z) => z.id)));
  const [expandedBeds, setExpandedBeds] = useState<Set<string>>(new Set());

  const filteredPlants = searchQuery
    ? plants.filter(
        (p) =>
          p.common_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.botanical_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.cultivar?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : null;

  const toggleZone = (id: string) =>
    setExpandedZones((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleBed = (id: string) =>
    setExpandedBeds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Beds without a zone
  const unzonedBeds = beds.filter((b) => !b.zone_id);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2">
        <span className="text-xl" aria-hidden="true">🌿</span>
        <span className="font-serif font-bold text-garden-800 dark:text-garden-300 text-lg tracking-tight">GardenMap</span>
        <button onClick={onSettingsOpen} aria-label="Settings" className="ml-auto text-gray-400 hover:text-gray-600 transition-colors">
          <Settings size={17} />
        </button>
      </div>

      {/* Search */}
      <div className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search plants…"
            className="w-full pl-8 pr-7 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:border-garden-400 focus:bg-white dark:focus:bg-gray-800 transition"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Search results */}
      {filteredPlants && (
        <div className="flex-1 overflow-y-auto">
          {filteredPlants.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No plants found</p>
          ) : (
            <div className="p-2 flex flex-col gap-0.5">
              {filteredPlants.map((plant) => (
                <PlantRow
                  key={plant.id}
                  plant={plant}
                  selected={plant.id === selectedPlantId}
                  onClick={() => onPlantClick(plant)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Zone/Bed tree */}
      {!filteredPlants && (
        <div className="flex-1 overflow-y-auto py-2">
          {zones.map((zone) => {
            const zoneBeds = beds.filter((b) => b.zone_id === zone.id);
            const expanded = expandedZones.has(zone.id);
            return (
              <div key={zone.id}>
                <button
                  onClick={() => toggleZone(zone.id)}
                  aria-expanded={expanded}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left group"
                >
                  {expanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: zone.color }}
                    aria-hidden="true"
                  />
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex-1 truncate">{zone.name}</span>
                  <span className="text-xs text-gray-400">{zoneBeds.length}</span>
                </button>

                {expanded && (
                  <div className="pl-6">
                    {zoneBeds.map((bed) => (
                      <BedTree
                        key={bed.id}
                        bed={bed}
                        plants={plants.filter((p) => p.bed_id === bed.id)}
                        selected={bed.id === selectedBedId}
                        selectedPlantId={selectedPlantId}
                        expanded={expandedBeds.has(bed.id)}
                        onToggle={() => toggleBed(bed.id)}
                        onBedClick={() => onBedClick(bed)}
                        onPlantClick={onPlantClick}
                      />
                    ))}
                    {zoneBeds.length === 0 && (
                      <p className="text-xs text-gray-400 px-3 py-1.5">No beds</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {unzonedBeds.length > 0 && (
            <div>
              <div className="flex items-center gap-2 px-3 py-2">
                <Layers size={14} className="text-gray-400" />
                <span className="text-sm font-semibold text-gray-500">Unassigned</span>
              </div>
              {unzonedBeds.map((bed) => (
                <BedTree
                  key={bed.id}
                  bed={bed}
                  plants={plants.filter((p) => p.bed_id === bed.id)}
                  selected={bed.id === selectedBedId}
                  selectedPlantId={selectedPlantId}
                  expanded={expandedBeds.has(bed.id)}
                  onToggle={() => toggleBed(bed.id)}
                  onBedClick={() => onBedClick(bed)}
                  onPlantClick={onPlantClick}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add actions */}
      <div className="px-3 py-3 border-t border-gray-100 dark:border-gray-800 flex flex-col gap-1.5">
        <Button variant="primary" size="sm" onClick={onAddPlant} className="w-full">
          <Leaf size={14} /> Add Plant
        </Button>
        <div className="flex gap-1.5">
          <Button variant="outline" size="sm" onClick={onAddBed} className="flex-1">
            <Plus size={14} /> Bed
          </Button>
          <Button variant="outline" size="sm" onClick={onAddZone} className="flex-1">
            <Plus size={14} /> Zone
          </Button>
        </div>
      </div>
    </div>
  );
}

function BedTree({
  bed,
  plants,
  selected,
  selectedPlantId,
  expanded,
  onToggle,
  onBedClick,
  onPlantClick,
}: {
  bed: Bed;
  plants: Plant[];
  selected: boolean;
  selectedPlantId: string | null;
  expanded: boolean;
  onToggle: () => void;
  onBedClick: () => void;
  onPlantClick: (p: Plant) => void;
}) {
  return (
    <div>
      <div className="flex items-center">
        <button
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse bed' : 'Expand bed'}
          className="p-1.5 min-w-[32px] min-h-[32px] flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>
        <button
          onClick={onBedClick}
          className={cn(
            'flex-1 flex items-center gap-2 py-1.5 px-1 rounded-lg text-sm transition-colors text-left',
            selected
              ? 'bg-garden-100 dark:bg-garden-900 text-garden-800 dark:text-garden-200 font-medium'
              : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300',
          )}
        >
          <span className="truncate">{bed.name}</span>
          {plants.length > 0 && <span className="text-xs text-gray-400 shrink-0">{plants.length}</span>}
        </button>
      </div>

      {expanded && plants.length > 0 && (
        <div className="pl-5 flex flex-col gap-0.5 mb-1">
          {plants.map((plant) => (
            <PlantRow
              key={plant.id}
              plant={plant}
              selected={plant.id === selectedPlantId}
              onClick={() => onPlantClick(plant)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PlantRow({
  plant,
  selected,
  onClick,
}: {
  plant: Plant;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 py-1 px-2 rounded-lg text-sm transition-colors text-left w-full',
        selected
          ? 'bg-garden-100 dark:bg-garden-900 text-garden-800 dark:text-garden-200 font-medium'
          : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400',
      )}
    >
      <span>{TYPE_EMOJI[plant.plant_type]}</span>
      <span className="flex-1 truncate">{plant.common_name}</span>
      <Badge className={cn(STATUS_BG[plant.status], 'text-[10px] px-1.5 py-px')}>
        {STATUS_LABELS[plant.status]}
      </Badge>
    </button>
  );
}
